import React from 'react';
import { Box, Button, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, MenuItem, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useFormContext, useFieldArray, Controller, FieldError } from 'react-hook-form';
import { WizardFormData } from '../schemas';

const AUTOGENERATE_RULE_OPTIONS = [
  { value: 0, label: 'None' },
  { value: 1, label: 'Letters only' },
  { value: 2, label: 'Numbers only' },
  { value: 3, label: 'Alphanumeric' },
];

function ParameterTable() {
  const {
    control,
    formState: { errors },
  } = useFormContext<WizardFormData>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'x1Parameters',
  });

  const handleAddRow = () => {
    append({
      tag: '',
      autogenerateRule: 3, // Alphanumeric
      length: 7,
      prefix: '',
      replacementMethod: 'dynamicParameter',
    });
  };

  const handleDeleteRow = (index: number) => {
    remove(index);
  };

  const getTableErrors = () => {
    const { x1Parameters } = errors;

    if (!x1Parameters) return [];

    const isArray = Array.isArray(x1Parameters);

    if (!isArray) return [x1Parameters.message];

    const errorMessages = x1Parameters.filter(Boolean).flatMap((item) => {
      
      const values = Object.values<any>(item);
      const messages = values.map((value) => value.message);

      return messages;
    });

    const emsgs = new Set(errorMessages);

    return Array.from(emsgs);
  };

  const tableErrors = getTableErrors();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle1">Parameters Configuration</Typography>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddRow} size="small">
          Add Row
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Tag</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Autogenerate Rule</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Length</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Prefix</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fields.map((field, index) => {
              const x1ParamsErrors = errors.x1Parameters as any;
              const tagError = x1ParamsErrors?.[index]?.tag?.message;
              const lengthError = x1ParamsErrors?.[index]?.length?.message;
              const autogenerateRuleError = x1ParamsErrors?.[index]?.autogenerateRule?.message;
              const prefixError = x1ParamsErrors?.[index]?.prefix?.message;

              return (
                <TableRow key={field.id}>
                  <TableCell>
                    <Controller
                      name={`x1Parameters.${index}.tag`}
                      control={control}
                      render={({ field: tagField }) => <TextField {...tagField} size="small" fullWidth error={!!tagError} />}
                    />
                  </TableCell>
                  <TableCell>
                    <Controller
                      name={`x1Parameters.${index}.autogenerateRule`}
                      control={control}
                      render={({ field: ruleField }) => (
                        <TextField {...ruleField} select size="small" fullWidth error={!!autogenerateRuleError}>
                          {AUTOGENERATE_RULE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    <Controller
                      name={`x1Parameters.${index}.length`}
                      control={control}
                      render={({ field: lengthField }) => (
                        <TextField
                          {...lengthField}
                          type="number"
                          size="small"
                          fullWidth
                          inputProps={{ min: 1 }}
                          error={!!lengthError}
                          value={lengthField.value ?? ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            lengthField.onChange(value === '' ? 0 : Number(value));
                          }}
                          onWheel={(e) => {
                            (e.target as HTMLInputElement).blur();
                          }}
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    <Controller
                      name={`x1Parameters.${index}.prefix`}
                      control={control}
                      render={({ field: prefixField }) => <TextField {...prefixField} size="small" fullWidth value={prefixField.value ?? ''} error={!!prefixError} />}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleDeleteRow(index)} color="error" size="small">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box>
        {tableErrors.map((error) => (
          <Typography variant="caption" color="error" key={error} sx={{ display: 'block' }}>
            {error}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

export default ParameterTable;
